// src/components/UserSpace/MessageUser.js - Version avec popup de détails utilisateur
import React, { useState, useEffect, useRef } from 'react';
import { messageService, userService } from '../../api';
import { useNavigate, Link } from 'react-router-dom';
import { 
  FaPaperPlane, 
  FaSmile, 
  FaPaperclip, 
  FaEllipsisV,
  FaCheckDouble,
  FaCheck,
  FaUser,
  FaUsers,
  FaSearch,
  FaArrowLeft,
  FaImage,
  FaMicrophone,
  FaCamera,
  FaLock,
  FaShieldAlt,
  FaCheckCircle,
  FaExclamationCircle,
  FaTimes,
  FaEnvelope,
  FaPhone,
  FaCalendarAlt,
  FaMapMarkerAlt,
  FaEdit,
  FaCommentDots,
  FaExchangeAlt,
  FaHistory
} from 'react-icons/fa';
import './MessageUser.css';

const MessageUser = () => {
  const [sentMessages, setSentMessages] = useState([]);
  const [receivedMessages, setReceivedMessages] = useState([]);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [replyContent, setReplyContent] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [showConversation, setShowConversation] = useState(false);
  const [showMobileList, setShowMobileList] = useState(true);
  const [notifications, setNotifications] = useState([]);
  const [currentUser, setCurrentUser] = useState({ id: 1, name: 'Moi' });
  
  const [showUserDetails, setShowUserDetails] = useState(false);
  const [selectedUserDetails, setSelectedUserDetails] = useState(null);
  const [loadingUserDetails, setLoadingUserDetails] = useState(false);
  const [conversationStats, setConversationStats] = useState(null);
  const [conversationUsers, setConversationUsers] = useState([]);
  
  const messagesEndRef = useRef(null);

  // Fonction pour ajouter une notification
  const addNotification = (message, type = 'info') => {
    const id = Date.now();
    setNotifications(prev => [...prev, { id, message, type }]);
    
    setTimeout(() => {
      setNotifications(prev => prev.filter(n => n.id !== id));
    }, 5000);
    
    return id;
  };

  // Fonction pour ouvrir les détails utilisateur avec les nouvelles APIs
  const handleShowUserDetails = async (userId) => {
    try {
      setLoadingUserDetails(true);
      
      // Utiliser la nouvelle API pour récupérer les détails avec statistiques
      const userData = await messageService.getConversationUser(userId);
      
      if (userData && userData.user) {
        setSelectedUserDetails(userData.user);
        setConversationStats(userData.statistiques || {});
        setShowUserDetails(true);
      } else {
        // Fallback: récupérer les infos de base via userService
        const users = await userService.getAll();
        const user = users.find(u => u.id === userId);
        
        if (user) {
          setSelectedUserDetails(user);
          setConversationStats({
            total_messages: 0,
            messages_envoyes: 0,
            messages_recus: 0
          });
          setShowUserDetails(true);
        }
      }
    } catch (err) {
      console.error('Erreur lors du chargement des détails:', err);
      
      // Fallback: récupérer les infos de base
      try {
        const users = await userService.getAll();
        const user = users.find(u => u.id === userId);
        
        if (user) {
          setSelectedUserDetails(user);
          setConversationStats({
            total_messages: 0,
            messages_envoyes: 0,
            messages_recus: 0
          });
          setShowUserDetails(true);
          addNotification('Détails basiques chargés', 'info');
        } else {
          throw new Error('Utilisateur non trouvé');
        }
      } catch (fallbackErr) {
        addNotification('Impossible de charger les détails utilisateur', 'error');
      }
    } finally {
      setLoadingUserDetails(false);
    }
  };

  // Fonction pour charger tous les utilisateurs avec qui on discute
  useEffect(() => {
    const loadConversationUsers = async () => {
      try {
        // Utiliser la nouvelle API pour récupérer les utilisateurs avec qui on a discuté
        const users = await messageService.getConversationUsers();
        setConversationUsers(users);
      } catch (err) {
        console.error('Erreur lors du chargement des utilisateurs de conversation:', err);
      }
    };
    
    if (sentMessages.length > 0 || receivedMessages.length > 0) {
      loadConversationUsers();
    }
  }, [sentMessages, receivedMessages]);

  // Fonction pour fermer le popup
  const handleCloseUserDetails = () => {
    setShowUserDetails(false);
    setSelectedUserDetails(null);
    setConversationStats(null);
  };

  // Fonction pour déterminer si un message est de l'utilisateur courant
  const isCurrentUserMessage = (message) => {
    return !message.isReceived || (message.expediteur_id === currentUser.id);
  };

  // Fusionner les conversations
  const mergeConversations = () => {
    const conversations = {};
    
    receivedMessages.forEach(msg => {
      const contactId = msg.expediteur_id;
      if (!conversations[contactId]) {
        conversations[contactId] = {
          contactId,
          contactName: msg.expediteur?.name || `Utilisateur ${msg.expediteur_id}`,
          lastMessage: msg,
          messages: []
        };
      }
      conversations[contactId].messages.push({...msg, isFromCurrentUser: false});
      if (new Date(msg.created_at) > new Date(conversations[contactId].lastMessage.created_at)) {
        conversations[contactId].lastMessage = msg;
      }
    });

    sentMessages.forEach(msg => {
      const contactId = msg.destinataire_id;
      if (!conversations[contactId]) {
        conversations[contactId] = {
          contactId,
          contactName: msg.destinataire?.name || `Utilisateur ${msg.destinataire_id}`,
          lastMessage: msg,
          messages: []
        };
      }
      conversations[contactId].messages.push({...msg, isFromCurrentUser: true});
      if (new Date(msg.created_at) > new Date(conversations[contactId].lastMessage.created_at)) {
        conversations[contactId].lastMessage = msg;
      }
    });

    return Object.values(conversations).sort((a, b) => 
      new Date(b.lastMessage.created_at) - new Date(a.lastMessage.created_at)
    );
  };

  // Charger les messages
  useEffect(() => {
    const loadMessages = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const [sent, received] = await Promise.all([
          messageService.getSent().catch(e => {
            console.error('Error loading sent messages:', e);
            return [];
          }),
          messageService.getReceived().catch(e => {
            console.error('Error loading received messages:', e);
            return [];
          })
        ]);
        
        setSentMessages(sent);
        setReceivedMessages(received);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    loadMessages();
  }, []);

  // Scroll vers le bas quand les messages changent
  useEffect(() => {
    if (messagesEndRef.current && selectedMessage) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [selectedMessage?.messages]);

  // Filtrer les conversations
  const conversations = mergeConversations();
  const filteredConversations = conversations.filter(conv => 
    conv.contactName.toLowerCase().includes(searchTerm.toLowerCase()) ||
    conv.lastMessage.contenu.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Sélectionner une conversation
  const handleSelectConversation = async (contactId) => {
    try {
      const contactMessages = [
        ...receivedMessages.filter(msg => msg.expediteur_id === contactId)
          .map(msg => ({ ...msg, isFromCurrentUser: false })),
        ...sentMessages.filter(msg => msg.destinataire_id === contactId)
          .map(msg => ({ ...msg, isFromCurrentUser: true }))
      ].sort((a, b) => new Date(a.created_at) - new Date(b.created_at));

      const contactConv = conversations.find(c => c.contactId === contactId);
      
      setSelectedMessage({
        contactId,
        contactName: contactConv.contactName,
        messages: contactMessages,
        lastMessage: contactConv.lastMessage
      });
      
      setReplyContent('');
      setShowConversation(true);
      if (window.innerWidth <= 768) {
        setShowMobileList(false);
      }
    } catch (err) {
      setError("Erreur lors de la récupération de la conversation");
      console.error(err);
    }
  };

  // Envoyer une réponse
  const handleReply = async () => {
    if (!selectedMessage || !replyContent.trim()) return;

    try {
      setLoading(true);
      
      addNotification('Envoi du message en cours...', 'info');
      
      const response = await messageService.send(replyContent, selectedMessage.contactId);
      
      if (response && response.notification_sent) {
        addNotification('✅ Message envoyé avec notification email', 'success');
      } else if (response && response.notification_sent === false) {
        addNotification('✅ Message envoyé (notification email échouée)', 'success');
      } else {
        addNotification('✅ Message envoyé', 'success');
      }
      
      const [sent, received] = await Promise.all([
        messageService.getSent(),
        messageService.getReceived()
      ]);
      
      setSentMessages(sent);
      setReceivedMessages(received);
      
      const newMessage = {
        id: response?.data?.id || Date.now(),
        contenu: replyContent,
        expediteur_id: currentUser.id,
        destinataire_id: selectedMessage.contactId,
        created_at: new Date().toISOString(),
        isFromCurrentUser: true,
        notification_sent: response?.notification_sent || false
      };
      
      setSelectedMessage(prev => ({
        ...prev,
        messages: [...prev.messages, newMessage],
        lastMessage: newMessage
      }));
      
      setReplyContent('');
      setError(null);
      
    } catch (err) {
      console.error('Erreur complète:', err);
      addNotification(`❌ Erreur: ${err.message || 'Échec de l\'envoi'}`, 'error');
      setError(err.message || 'Erreur lors de l\'envoi du message');
    } finally {
      setLoading(false);
    }
  };

  // Fonctions d'utilité
  const formatTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
  };

  const formatMessageDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', { 
      weekday: 'long', 
      day: 'numeric', 
      month: 'long',
      year: 'numeric'
    });
  };

  const groupMessagesByDate = (messages) => {
    const groups = {};
    messages.forEach(message => {
      const date = new Date(message.created_at).toDateString();
      if (!groups[date]) {
        groups[date] = [];
      }
      groups[date].push(message);
    });
    return groups;
  };

  const handleBackToList = () => {
    setShowMobileList(true);
    setSelectedMessage(null);
  };

  // Récupérer l'initiale pour l'avatar
  const getInitial = (name) => {
    return name ? name.charAt(0).toUpperCase() : 'U';
  };

  // Obtenir la couleur de l'avatar basée sur l'ID
  const getAvatarColor = (userId) => {
    const colors = [
      '#0d3b66', // Bleu foncé
      '#00a884', // Vert WhatsApp
      '#667781', // Gris bleuté
      '#8696a0', // Gris clair
      '#0d3d2c', // Vert foncé
      '#2a3942', // Gris foncé
      '#005c4b', // Vert émeraude
      '#202c33'  // Gris très foncé
    ];
    return colors[userId % colors.length];
  };

  if (loading && sentMessages.length === 0 && receivedMessages.length === 0) {
    return (
      <div className="msgapp-loading">
        <div className="msgapp-spinner"></div>
        <p>Chargement des messages...</p>
      </div>
    );
  }

  return (
    
    <div className="msgapp-container">
       
      {/* Header principal */}
      <div className="msgapp-header">
          
        {(!showConversation || window.innerWidth > 768) && (
          <>
            <div className="msgapp-avatar">
              <div 
                className="msgapp-avatar-placeholder"
                style={{ backgroundColor: getAvatarColor(currentUser.id) }}
              >
                {getInitial(currentUser.name)}
              </div>
            </div>
            <div className="msgapp-title">
              <h2>Messagerie</h2>
              <span>Connecté en tant que {currentUser.name}</span>
            </div>
            <div className="msgapp-actions">
              <button className="msgapp-action-btn">
                <FaSearch />
              </button>
              <button className="msgapp-action-btn">
                <FaEllipsisV />
              </button>
            </div>
          </>
        )}
        
        {showConversation && window.innerWidth <= 768 && (
          <div className="msgapp-conversation-header-mobile">
            <button className="msgapp-back-btn" onClick={handleBackToList}>
              <FaArrowLeft />
            </button>
            <div className="msgapp-contact-info">
              <div 
                className="msgapp-contact-avatar clickable-avatar"
                style={{ backgroundColor: getAvatarColor(selectedMessage?.contactId) }}
                onClick={() => selectedMessage?.contactId && handleShowUserDetails(selectedMessage.contactId)}
                title="Voir le profil"
              >
                {getInitial(selectedMessage?.contactName)}
              </div>
              <div className="msgapp-contact-details">
                <span className="msgapp-contact-name">
                  {selectedMessage?.contactName}
                </span>
                <span className="msgapp-contact-status">en ligne</span>
              </div>
            </div>
            <div className="msgapp-header-actions">
              <button 
                className="msgapp-action-btn"
                onClick={() => selectedMessage?.contactId && handleShowUserDetails(selectedMessage.contactId)}
                title="Voir le profil"
              >
                <FaUser />
              </button>
              <button className="msgapp-action-btn">
                <FaEllipsisV />
              </button>
            </div>
          </div>
        )}
      </div>

      {error && <div className="msgapp-error">{error}</div>}

      {/* Notifications */}
      <div className="notification-container">
        {notifications.map(notif => (
          <div key={notif.id} className={`notification-toast ${notif.type}`}>
            <div className="toast-content">
              {notif.type === 'success' && <FaCheckCircle className="toast-icon" />}
              {notif.type === 'error' && <FaExclamationCircle className="toast-icon" />}
              {notif.type === 'info' && <FaEnvelope className="toast-icon" />}
              <p>{notif.message}</p>
              <button 
                className="toast-close"
                onClick={() => setNotifications(prev => prev.filter(n => n.id !== notif.id))}
              >
                <FaTimes />
              </button>
            </div>
          </div>
        ))}
      </div>

      <div className="msgapp-layout">
        {/* Liste des conversations */}
        {(showMobileList || window.innerWidth > 768) && (
          <div className="msgapp-conversations-list">
            <div className="msgapp-search-container">
              <div className="msgapp-search-wrapper">
                <FaSearch className="msgapp-search-icon" />
                <input
                  type="text"
                  placeholder="Rechercher une conversation..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="msgapp-search-input"
                />
              </div>
            </div>

            <div className="msgapp-conversations">
              {filteredConversations.length === 0 ? (
                <div className="msgapp-empty">
                  <div className="msgapp-empty-icon">
                    <FaUsers />
                  </div>
                  <p>{searchTerm ? 'Aucune conversation trouvée' : 'Aucune conversation'}</p>
                </div>
              ) : (
                filteredConversations.map(conv => (
                  <div 
                    key={conv.contactId}
                    className={`msgapp-conversation-item ${selectedMessage?.contactId === conv.contactId ? 'msgapp-conversation-selected' : ''}`}
                    onClick={() => handleSelectConversation(conv.contactId)}
                  >
                    <div className="msgapp-conversation-avatar">
                      <div 
                        className="msgapp-avatar clickable-avatar"
                        style={{ backgroundColor: getAvatarColor(conv.contactId) }}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleShowUserDetails(conv.contactId);
                        }}
                        title="Voir le profil"
                      >
                        {getInitial(conv.contactName)}
                      </div>
                      {conv.unread && <div className="msgapp-unread-indicator"></div>}
                    </div>
                    <div className="msgapp-conversation-content">
                      <div className="msgapp-conversation-header">
                        <span className="msgapp-contact-name">
                          {conv.contactName}
                        </span>
                        <span className="msgapp-message-time">
                          {formatTime(conv.lastMessage.created_at)}
                        </span>
                      </div>
                      <div className="msgapp-message-preview">
                        <div className="msgapp-preview-content">
                          {conv.lastMessage.isFromCurrentUser ? (
                            <span className="msgapp-sent-indicator">→</span>
                          ) : (
                            <span className="msgapp-received-indicator">←</span>
                          )}
                          {conv.lastMessage.contenu.substring(0, 40)}
                          {conv.lastMessage.contenu.length > 40 && '...'}
                        </div>
                        {conv.unread && (
                          <span className="msgapp-unread-badge">1</span>
                        )}
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        )}

        {/* Zone de conversation */}
        {(showConversation || window.innerWidth > 768) && (
          <div className="msgapp-conversation-area">
            {selectedMessage ? (
              <>
                {window.innerWidth > 768 && (
                  <div className="msgapp-conversation-header">
                    <div className="msgapp-contact-info">
                      <div 
                        className="msgapp-contact-avatar clickable-avatar"
                        style={{ backgroundColor: getAvatarColor(selectedMessage.contactId) }}
                        onClick={() => handleShowUserDetails(selectedMessage.contactId)}
                        title="Voir le profil"
                      >
                        {getInitial(selectedMessage.contactName)}
                      </div>
                      <div className="msgapp-contact-details">
                        <span className="msgapp-contact-name">
                          {selectedMessage.contactName}
                        </span>
                        <span className="msgapp-contact-status">en ligne</span>
                      </div>
                    </div>
                    <div className="msgapp-header-actions">
                      <button 
                        className="msgapp-action-btn"
                        onClick={() => handleShowUserDetails(selectedMessage.contactId)}
                        title="Voir le profil"
                      >
                        <FaUser />
                      </button>
                      <button className="msgapp-action-btn">
                        <FaEllipsisV />
                      </button>
                    </div>
                  </div>
                )}

                <div className="msgapp-messages-container">
                  {Object.entries(groupMessagesByDate(selectedMessage.messages)).map(([date, messages]) => (
                    <div key={date} className="msgapp-date-group">
                      <div className="msgapp-message-date">
                        {formatMessageDate(messages[0].created_at)}
                      </div>
                      <div className="msgapp-messages-group">
                        {messages.map((msg, index) => {
                          const isCurrentUser = msg.isFromCurrentUser;
                          const showAvatar = index === 0 || 
                            (messages[index - 1] && 
                             messages[index - 1].isFromCurrentUser !== isCurrentUser);
                          
                          return (
                            <div 
                              key={msg.id}
                              className={`msgapp-message-wrapper ${isCurrentUser ? 'msgapp-wrapper-sent' : 'msgapp-wrapper-received'}`}
                            >
                              {!isCurrentUser && showAvatar && (
                                <div 
                                  className="msgapp-message-avatar clickable-avatar"
                                  style={{ backgroundColor: getAvatarColor(selectedMessage.contactId) }}
                                  onClick={() => handleShowUserDetails(selectedMessage.contactId)}
                                  title="Voir le profil"
                                >
                                  {getInitial(selectedMessage.contactName)}
                                </div>
                              )}
                              
                              <div className="msgapp-message-content-container">
                                {!isCurrentUser && showAvatar && (
                                  <div className="msgapp-sender-name">
                                    {selectedMessage.contactName}
                                  </div>
                                )}
                                
                                <div 
                                  className={`msgapp-message-bubble ${isCurrentUser ? 'msgapp-message-sent' : 'msgapp-message-received'}`}
                                >
                                  <div className="msgapp-message-text">
                                    {msg.contenu}
                                  </div>
                                  <div className="msgapp-message-footer">
                                    <span className="msgapp-message-time">
                                      {formatTime(msg.created_at)}
                                    </span>
                                    {isCurrentUser && (
                                      <div className="msgapp-message-status">
                                        {msg.read ? (
                                          <FaCheckDouble className="msgapp-read-icon msgapp-read" />
                                        ) : (
                                          <FaCheck className="msgapp-sent-icon" />
                                        )}
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </div>

                              {isCurrentUser && showAvatar && (
                                <div 
                                  className="msgapp-message-avatar msgapp-current-user-avatar"
                                  style={{ backgroundColor: getAvatarColor(currentUser.id) }}
                                >
                                  {getInitial(currentUser.name)}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                  <div ref={messagesEndRef} />
                </div>

                <div className="msgapp-reply-container">
                  <div className="msgapp-reply-input-container">
                    <button className="msgapp-input-action">
                      <FaSmile />
                    </button>
                    <button className="msgapp-input-action">
                      <FaPaperclip />
                    </button>
                    <button className="msgapp-input-action">
                      <FaImage />
                    </button>
                    <div className="msgapp-input-wrapper">
                      <textarea
                        value={replyContent}
                        onChange={(e) => setReplyContent(e.target.value)}
                        placeholder="Écrivez votre message..."
                        className="msgapp-reply-input"
                        disabled={loading}
                        rows="1"
                        onKeyPress={(e) => {
                          if (e.key === 'Enter' && !e.shiftKey) {
                            e.preventDefault();
                            handleReply();
                          }
                        }}
                      />
                      <div className="msgapp-input-actions">
                        {replyContent.trim() ? (
                          <button 
                            onClick={handleReply}
                            disabled={loading}
                            className="msgapp-send-btn"
                          >
                            {loading ? (
                              <div className="msgapp-send-spinner"></div>
                            ) : (
                              <FaPaperPlane className="msgapp-send-icon" />
                            )}
                          </button>
                        ) : (
                          <button className="msgapp-input-action">
                            <FaMicrophone />
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </>
            ) : window.innerWidth > 768 ? (
              <div className="msgapp-welcome">
                <div className="msgapp-welcome-icon">
                  <FaUsers />
                </div>
                <h3>Votre messagerie</h3>
                <p>Sélectionnez une conversation pour commencer à discuter</p>
                <div className="msgapp-welcome-features">
                  <div className="msgapp-feature">
                    <FaLock />
                    <span>Messages sécurisés</span>
                  </div>
                  <div className="msgapp-feature">
                    <FaShieldAlt />
                    <span>Vos messages sont privés</span>
                  </div>
                </div>
              </div>
            ) : null}
          </div>
        )}
      </div>

      {/* Popup des détails utilisateur */}
      {showUserDetails && (
        <div className="msgapp-user-details-overlay">
          <div className="msgapp-user-details-modal">
            <div className="msgapp-user-details-header">
              <h3>Profil utilisateur</h3>
              <button 
                className="msgapp-user-details-close"
                onClick={handleCloseUserDetails}
              >
                <FaTimes />
              </button>
            </div>
            
            {loadingUserDetails ? (
              <div className="msgapp-user-details-loading">
                <div className="msgapp-spinner"></div>
                <p>Chargement des informations...</p>
              </div>
            ) : (
              <div className="msgapp-user-details-content">
                <div className="msgapp-user-details-avatar-section">
                  <div 
                    className="msgapp-user-details-avatar"
                    style={{ backgroundColor: getAvatarColor(selectedUserDetails?.id) }}
                  >
                    {getInitial(selectedUserDetails?.name)}
                  </div>
                  <div className="msgapp-user-details-name-section">
                    <h4 className="msgapp-user-details-name">
                      {selectedUserDetails?.name}
                    </h4>
                    <span className="msgapp-user-details-email">
                      {selectedUserDetails?.email}
                    </span>
                  </div>
                </div>
                
                <div className="msgapp-user-details-info">
                  <div className="msgapp-user-details-section">
                    <h5>Informations générales</h5>
                    <div className="msgapp-user-details-grid">
                      <div className="msgapp-user-detail-item">
                        <FaUser className="msgapp-detail-icon" />
                        <span className="msgapp-detail-label">ID utilisateur:</span>
                        <span className="msgapp-detail-value">{selectedUserDetails?.id}</span>
                      </div>
                      <div className="msgapp-user-detail-item">
                        <FaEdit className="msgapp-detail-icon" />
                        <span className="msgapp-detail-label">Rôle:</span>
                        <span className="msgapp-detail-value">{selectedUserDetails?.role || 'Utilisateur'}</span>
                      </div>
                      <div className="msgapp-user-detail-item">
                        <FaCheckCircle className="msgapp-detail-icon" />
                        <span className="msgapp-detail-label">Email vérifié:</span>
                        <span className="msgapp-detail-value">
                          {selectedUserDetails?.email_verified_at ? '✅ Oui' : '❌ Non'}
                        </span>
                      </div>
                      <div className="msgapp-user-detail-item">
                        <FaCalendarAlt className="msgapp-detail-icon" />
                        <span className="msgapp-detail-label">Date d'inscription:</span>
                        <span className="msgapp-detail-value">
                          {selectedUserDetails?.created_at 
                            ? new Date(selectedUserDetails.created_at).toLocaleDateString('fr-FR')
                            : 'Non disponible'}
                        </span>
                      </div>
                      {selectedUserDetails?.telephone && (
                        <div className="msgapp-user-detail-item">
                          <FaPhone className="msgapp-detail-icon" />
                          <span className="msgapp-detail-label">Téléphone:</span>
                          <span className="msgapp-detail-value">{selectedUserDetails.telephone}</span>
                        </div>
                      )}
                      {selectedUserDetails?.ville && (
                        <div className="msgapp-user-detail-item">
                          <FaMapMarkerAlt className="msgapp-detail-icon" />
                          <span className="msgapp-detail-label">Ville:</span>
                          <span className="msgapp-detail-value">{selectedUserDetails.ville}</span>
                        </div>
                      )}
                    </div>
                  </div>
                  
                  {conversationStats && (
                    <div className="msgapp-user-details-section">
                      <h5>Statistiques de conversation</h5>
                      <div className="msgapp-user-stats">
                        <div className="msgapp-user-stat">
                          <FaCommentDots className="msgapp-stat-icon" />
                          <span className="msgapp-stat-label">Messages échangés:</span>
                          <span className="msgapp-stat-value">{conversationStats.total_messages || 0}</span>
                        </div>
                        <div className="msgapp-user-stat">
                          <FaExchangeAlt className="msgapp-stat-icon" />
                          <span className="msgapp-stat-label">Envoyés vers vous:</span>
                          <span className="msgapp-stat-value">{conversationStats.messages_envoyes || 0}</span>
                        </div>
                        <div className="msgapp-user-stat">
                          <FaHistory className="msgapp-stat-icon" />
                          <span className="msgapp-stat-label">Reçus de vous:</span>
                          <span className="msgapp-stat-value">{conversationStats.messages_recus || 0}</span>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  <div className="msgapp-user-details-actions">
                    <button 
                      className="msgapp-user-action-btn msgapp-action-primary"
                      onClick={() => {
                        handleCloseUserDetails();
                        if (selectedMessage?.contactId !== selectedUserDetails?.id) {
                          handleSelectConversation(selectedUserDetails?.id);
                        }
                      }}
                    >
                      <FaEnvelope />
                      Envoyer un message
                    </button>
                    <button 
                      className="msgapp-user-action-btn msgapp-action-secondary"
                      onClick={handleCloseUserDetails}
                    >
                      <FaTimes />
                      Fermer
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default MessageUser;